import { customElement } from "lit/decorators.js";
import { html, TemplateResult } from "lit";
import { Pagination, SavedAnnotation, savedAnnotationState } from "@/state/SavedAnnotations";
import "@/components/pagination";
import { searchQueryState } from "@/state/SearchQuery";
import { DodonaElement } from "@/components/meta/dodona_element";
import { I18n } from "@/i18n/i18n";
import { FilterOptions } from "@/components/search/filter_element";
import { search } from "@/search";

/**
 * This component represents a list of saved annotations
 *
 * @element d-saved-annotation-list
 */
@customElement("d-saved-annotation-list")
export class SavedAnnotationList extends DodonaElement {
    get queryParams(): Map<string, string> {
        return searchQueryState.queryParams;
    }

    get arrayQueryParams(): Map<string, string[]> {
        return searchQueryState.arrayQueryParams;
    }

    lastSavedAnnotations: SavedAnnotation[] = [];
    get savedAnnotations(): SavedAnnotation[] {
        const savedAnnotations = savedAnnotationState.getList(this.queryParams, this.arrayQueryParams);
        if (savedAnnotations === undefined) {
            // return last saved annotations if the updated list is not yet available
            return this.lastSavedAnnotations;
        }
        this.lastSavedAnnotations = savedAnnotations;
        return savedAnnotations;
    }

    lastPagination: Pagination = { current_page: 1, total_pages: 1 };
    get pagination(): Pagination {
        const pagination = savedAnnotationState.getPagination(this.queryParams, this.arrayQueryParams);
        if (pagination === undefined) {
            // return last pagination if the updated pagination is not yet available
            return this.lastPagination;
        }
        this.lastPagination = pagination;
        return pagination;
    }

    lastFilters: FilterOptions = { dropdowns: [], switches: [] };
    get filters(): FilterOptions {
        const filters = savedAnnotationState.getFilters(this.queryParams, this.arrayQueryParams);
        if (filters === undefined) {
            // return last filters if the updated filters are not yet available
            return this.lastFilters;
        }
        this.lastFilters = filters;
        return filters;
    }

    render(): TemplateResult {
        search.updateFilters(this.filters);
        return this.savedAnnotations.length > 0
            ? html`
                  <div class="table-scroll-wrapper">
                      <table class="table table-index table-resource">
                          <thead>
                              <tr>
                                  <th scope="col" title="${I18n.t("js.saved_annotation.annotations_count")}">
                                      <d-sort-button column="annotations_count" default="DESC">#</d-sort-button>
                                  </th>
                                  <th scope="col">
                                      <d-sort-button column="title">
                                          ${I18n.t("js.saved_annotation.title")}
                                      </d-sort-button>
                                  </th>
                                  <th scope="col">
                                      <d-sort-button column="annotation_text">
                                          ${I18n.t("js.saved_annotation.annotation_text")}
                                      </d-sort-button>
                                  </th>
                                  <th scope="col">${I18n.t("js.saved_annotation.course")}</th>
                                  <th scope="col">${I18n.t("js.saved_annotation.exercise")}</th>
                              </tr>
                          </thead>
                          <tbody>
                              ${this.savedAnnotations.map(
                                  (sa) => html`
                                      <tr>
                                          <td>${sa.annotations_count}</td>
                                          <td class="ellipsis-overflow" title="${sa.title}">
                                              <a href="${sa.url}" class="fw-medium">${sa.title}</a>
                                          </td>
                                          <td class="ellipsis-overflow" title="${sa.annotation_text}">
                                              ${sa.annotation_text}
                                          </td>
                                          <td>
                                              ${
                                                  sa.course
                                                      ? html`<d-filter-button param="course_id" value="${sa.course.id}"
                                                            >${sa.course.name}</d-filter-button
                                                        >`
                                                      : ""
                                              }
                                          </td>
                                          <td>
                                              ${
                                                  sa.exercise
                                                      ? html`<d-filter-button
                                                            param="exercise_id"
                                                            value="${sa.exercise.id}"
                                                            >${sa.exercise.name}</d-filter-button
                                                        >`
                                                      : ""
                                              }
                                          </td>
                                      </tr>
                                  `,
                              )}
                          </tbody>
                      </table>
                  </div>
                  <d-pagination
                      .total=${this.pagination.total_pages}
                      .current=${this.pagination.current_page}
                  ></d-pagination>
              `
            : html``;
    }
}
